import { ChevronDown } from 'lucide-react';
import {
  cloneElement,
  forwardRef,
  HTMLAttributes,
  isValidElement,
  ReactNode,
  SelectHTMLAttributes,
  useId,
} from 'react';

import { cn } from './cn';

export interface FieldProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  label: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}

/** A labelled form control with optional hint and error text. */
export const Field = ({ children, className, error, hint, htmlFor, label, ...props }: FieldProps) => {
  const generatedId = useId();
  // Label a lone control automatically when the caller gave neither an id nor htmlFor.
  const needsId = !htmlFor && isValidElement<{ id?: string }>(children) && !children.props.id;
  const controlId =
    htmlFor ?? (needsId ? generatedId : isValidElement<{ id?: string }>(children) ? children.props.id : undefined);
  return (
    <div className={cn('flex min-w-0 flex-col gap-1.5', className)} {...props}>
      {label !== '' && (
        <label htmlFor={controlId} className="m-0 text-xs font-medium text-ink-muted">
          {label}
        </label>
      )}
      {needsId && isValidElement<{ id?: string }>(children) ? cloneElement(children, { id: generatedId }) : children}
      {hint && !error && <p className="m-0 text-xs text-ink-muted">{hint}</p>}
      {error && (
        <p role="alert" className="m-0 text-xs text-critical">
          {error}
        </p>
      )}
    </div>
  );
};

export interface NativeSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
  /** Sizing for the control as a whole. Width set on `className` would leave the chevron behind. */
  wrapperClassName?: string;
}

/** A styled native `<select>`, for short option lists where the platform picker is enough. */
export const NativeSelect = forwardRef<HTMLSelectElement, NativeSelectProps>(
  ({ className, invalid, wrapperClassName, ...props }, ref) => (
    <span className={cn('relative block w-full', wrapperClassName)}>
      <select
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          'h-9 w-full appearance-none rounded-auditor border border-line-strong bg-surface py-0 pl-2.5 pr-9 text-sm text-ink',
          'focus-visible:border-brand focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand/20',
          'disabled:cursor-not-allowed disabled:bg-canvas disabled:opacity-60',
          'aria-[invalid=true]:border-critical',
          className,
        )}
        {...props}
      />
      <ChevronDown
        aria-hidden="true"
        className="pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-ink-muted"
      />
    </span>
  ),
);
NativeSelect.displayName = 'NativeSelect';
